import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPublicProfile } from '../hooks/useFirestore';
import { exportToExcelPublic } from '../utils/excelExport';
import { Icons } from './Icons';
import MultiSelectFilter from './MultiSelectFilter';
import { locationMatchesQuery } from '../utils/usStates';
import { useAuth } from '../contexts/AuthContext';
import './MultiSelectFilter.css';
import './PublicView.css';

const STATUS_COLORS = { 'Not Applied': 'status-not-applied', Applied: 'status-applied', Interviewing: 'status-interviewing', Offer: 'status-offer', Rejected: 'status-rejected' };

function isAbbreviationMatch(query, companyName) {
  if (query.length < 2 || !/^[a-zA-Z]+$/.test(query)) return false;
  const nameWords = companyName.split(/\s+/).filter(w => w.length > 0);
  if (nameWords.length < 2) return false;
  const abbreviation = nameWords.map(w => w[0].toLowerCase()).join('');
  return abbreviation === query.toLowerCase() || abbreviation.startsWith(query.toLowerCase());
}

export default function PublicView() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile: ownerProfile } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [viewerEmail, setViewerEmail] = useState('');
  const [viewerCode, setViewerCode] = useState('');
  const [accessLevel, setAccessLevel] = useState('public');
  const [permissions, setPermissions] = useState({ stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [contactFilters, setContactFilters] = useState([]);
  const [stateFilters, setStateFilters] = useState([]);
  const [cityFilters, setCityFilters] = useState([]);
  const [updateMsgFilter, setUpdateMsgFilter] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [accessFeedback, setAccessFeedback] = useState(null);
  const [viewLayout, setViewLayout] = useState('list');
  const [showSearchTip, setShowSearchTip] = useState(true);
  const [readability, setReadability] = useState('standard');
  const [isAccessCode, setIsAccessCode] = useState(false);

  useEffect(() => { if (data?.profile) document.title = `${data.profile.firstName}'s Status | TT`; }, [data]);
  useEffect(() => { document.body.setAttribute('data-readability', readability); return () => document.body.removeAttribute('data-readability'); }, [readability]);

  useEffect(() => { loadProfile(); }, [username]);

  async function loadProfile() {
    setLoading(true);
    const result = await fetchPublicProfile(username);
    if (result) {
      setData(result);
      document.title = `${result.profile.firstName}'s Status | TT`;
      if (result.profile.showSearchTip === false) setShowSearchTip(false);
      if (result.profile.publicPermissions) setPermissions(result.profile.publicPermissions);
    } else { setNotFound(true); }
    setLoading(false);
  }

  function checkAccess() {
    if (!data) return;
    const { profile } = data;
    const email = viewerEmail.trim().toLowerCase();
    setAccessFeedback(null);
    setIsAccessCode(false);

    const networks = profile.networks || [];
    for (const net of networks) {
      if (email && net.emails?.includes(email)) {
        if (net.enabled === false) { setAccessFeedback('paused'); return; }
        setAccessLevel('authorized'); setPermissions(net.permissions || {}); setAccessFeedback('granted'); return;
      }
    }
    if (email && profile.network1?.emails?.includes(email)) {
      if (profile.network1.enabled === false) { setAccessFeedback('paused'); return; }
      setAccessLevel('authorized'); setPermissions(profile.network1.permissions || {}); setAccessFeedback('granted'); return;
    }
    if (email && profile.network2?.emails?.includes(email)) {
      if (profile.network2.enabled === false) { setAccessFeedback('paused'); return; }
      setAccessLevel('authorized'); setPermissions(profile.network2.permissions || {}); setAccessFeedback('granted'); return;
    }
    if (viewerCode && profile.accessCode?.code && viewerCode === profile.accessCode.code) {
      if (profile.accessCode.enabled === false) { setAccessFeedback('paused'); return; }
      setAccessLevel('authorized'); setIsAccessCode(true);
      setPermissions(profile.accessCode.permissions || { stats: true, companies: true, roles: true, contacts: true, notes: true });
      setAccessFeedback('granted'); return;
    }
    setAccessLevel('public');
    setPermissions(profile.publicPermissions || { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false });
    if (email || viewerCode) setAccessFeedback('denied');
  }

  function parseLocation(loc) {
    if (!loc) return { state: '', city: '' };
    if (loc === 'Remote') return { state: 'Remote', city: '' };
    const parts = loc.split('·').map(p => p.trim());
    return { state: parts[0] || '', city: parts[1] || '' };
  }

  const hasActiveFilters = statusFilters.length > 0 || contactFilters.length > 0 || stateFilters.length > 0 || cityFilters.length > 0 || updateMsgFilter;

  // Role-level + contact-level filtering
  const { filteredCompanies, filteredRoles, filteredContacts, uniqueStates, uniqueCities } = useMemo(() => {
    if (!data) return { filteredCompanies: [], filteredRoles: [], filteredContacts: [], uniqueStates: [], uniqueCities: [] };
    let visibleRoles = [...data.roles];
    let visibleContacts = [...data.contacts];
    let visibleCompanies = [...data.companies];

    // Compute unique states/cities
    const statesSet = new Set();
    const citiesSet = new Set();
    data.roles.forEach(r => {
      const { state, city } = parseLocation(r.location);
      if (state) statesSet.add(state);
      if (city) citiesSet.add(city);
    });

    if (statusFilters.length > 0 && permissions.liveStatus) visibleRoles = visibleRoles.filter(r => statusFilters.includes(r.status));

    if (stateFilters.length > 0) {
      visibleRoles = visibleRoles.filter(r => {
        const { state } = parseLocation(r.location);
        return stateFilters.includes(state);
      });
    }
    if (cityFilters.length > 0) {
      visibleRoles = visibleRoles.filter(r => {
        const { city } = parseLocation(r.location);
        return cityFilters.includes(city);
      });
    }

    if (contactFilters.length > 0) {
      visibleContacts = visibleContacts.filter(c => contactFilters.includes(c.linkedinStatus));
      const matchingRoleIds = new Set(visibleContacts.map(c => c.roleId));
      visibleRoles = visibleRoles.filter(r => matchingRoleIds.has(r.id));
    }

    if (updateMsgFilter && permissions.updateMessage) {
      const companyIdsWithUpdateMsg = new Set(visibleRoles.filter(r => r.updateMessage && r.updateMessage.trim()).map(r => r.companyId));
      visibleRoles = visibleRoles.filter(r => r.updateMessage && r.updateMessage.trim());
      visibleCompanies = visibleCompanies.filter(c => companyIdsWithUpdateMsg.has(c.id));
    }

    if (hasActiveFilters) {
      const companyIds = new Set(visibleRoles.map(r => r.companyId));
      visibleCompanies = visibleCompanies.filter(c => companyIds.has(c.id));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const matchingContactCompanyIds = visibleContacts.filter(c => c.name.toLowerCase().includes(q) || (c.designation || '').toLowerCase().includes(q)).map(c => c.companyId);
      const matchingLocationCompanyIds = visibleRoles.filter(r => locationMatchesQuery(r.location, q)).map(r => r.companyId);
      visibleCompanies = visibleCompanies.filter(c => {
        const name = c.name.toLowerCase();
        if (name.includes(q)) return true;
        if (isAbbreviationMatch(q, c.name)) return true;
        if (visibleRoles.filter(r => r.companyId === c.id).some(r => r.title.toLowerCase().includes(q))) return true;
        if (matchingContactCompanyIds.includes(c.id)) return true;
        if (matchingLocationCompanyIds.includes(c.id)) return true;
        return false;
      });
    }

    return { filteredCompanies: visibleCompanies, filteredRoles: visibleRoles, filteredContacts: visibleContacts, uniqueStates: [...statesSet].sort(), uniqueCities: [...citiesSet].sort() };
  }, [data, searchQuery, statusFilters, contactFilters, stateFilters, cityFilters, hasActiveFilters, updateMsgFilter, permissions]);

  if (loading) return <div className="pv-loading"><div className="loading-spinner-lg" /><p>Loading profile...</p></div>;

  if (notFound) {
    return (
      <div className="pv-not-found">
        <div className="logo-mark" onClick={() => navigate('/')} style={{cursor:'pointer'}}>T T</div>
        <h2>Profile Not Found</h2><p>The username "{username}" doesn't exist on TrackThrough.</p>
        <a href="/" className="pv-home-link">Go to TrackThrough</a>
      </div>
    );
  }

  const { profile, companies, roles, contacts } = data;
  const isOwner = currentUser && ownerProfile?.username?.toLowerCase() === username?.toLowerCase();

  if (profile.publicEnabled === false && accessLevel === 'public') {
    return (
      <div className="pv-paused">
        <div className="logo-mark" onClick={() => navigate('/')} style={{cursor:'pointer'}}>T T</div>
        <div className="pv-paused-icon">🌿</div>
        <h2>{profile.firstName} has paused sharing for now.</h2>
        <p>Check back later. They'll be back when they're ready.</p>
        <div className="pv-paused-access">
          <p className="pv-paused-hint">Have authorized access? Enter your email or code below.</p>
          <div className="pv-access-check">
            <input type="email" value={viewerEmail} onChange={e => setViewerEmail(e.target.value)} placeholder="Enter your email" className="pv-email-input" onKeyDown={e => { if (e.key === 'Enter') checkAccess(); }} />
            <input type="text" value={viewerCode} onChange={e => setViewerCode(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="Code" className="pv-code-input" maxLength={4} inputMode="numeric" onKeyDown={e => { if (e.key === 'Enter') checkAccess(); }} />
            <button className="pv-check-btn" onClick={checkAccess}>Verify</button>
          </div>
        </div>
        <a href="/" className="pv-home-link" style={{marginTop: 24}}>Go to TrackThrough</a>
      </div>
    );
  }

  const stats = {
    totalCompanies: companies.length, totalRoles: roles.length,
    totalContacts: contacts.length, connectedContacts: contacts.filter(c => c.linkedinStatus === 'Connected').length,
  };
  const showDates = profile.showDates !== false;
  const canExport = isAccessCode;

  function handleThirdPartyExport() {
    exportToExcelPublic(filteredCompanies, filteredRoles, filteredContacts, profile.firstName || 'Export', permissions, {
      showDates
    });
  }

  return (
    <div className="public-view">
      <header className="pv-header">
        <div className="pv-header-inner">
          <div className="pv-header-logo" onClick={() => navigate('/')} style={{cursor:'pointer'}}>
            <div className="logo-mark-sm">T T</div>
            <span className="pv-title">TrackThrough</span>
          </div>
          <div className="pv-header-right">
            <div className="readability-toggle">
              <button className={`readability-btn ${readability === 'standard' ? 'active' : ''}`} onClick={() => setReadability('standard')} title="Standard">Aa</button>
              <button className={`readability-btn enhanced ${readability === 'enhanced' ? 'active' : ''}`} onClick={() => setReadability('enhanced')} title="Enhanced">Aa</button>
            </div>
          </div>
        </div>
      </header>

      <div className="pv-profile">
        <div className="pv-avatar">{profile.firstName?.[0]?.toUpperCase() || 'U'}</div>
        <h1 className="pv-name">{profile.firstName}'s Status</h1>
        <p className="pv-access-level">Viewing as: <span className={`pv-access-badge ${accessLevel}`}>{accessLevel === 'authorized' ? 'Authorized' : 'Public'}</span></p>
        {(profile.showNetworkEmail === true || profile.showNetworkPhone === true) && (
          <div className="pv-contact-info">
            {profile.showNetworkEmail === true && profile.networkEmail && (
              <a href={`mailto:${profile.networkEmail}`} className="pv-contact-item"><strong>✉ {profile.networkEmail}</strong></a>
            )}
            {profile.showNetworkPhone === true && profile.networkPhone && (
              <a href={`tel:${profile.networkPhone}`} className="pv-contact-item"><strong>📞 {profile.networkPhone}</strong></a>
            )}
          </div>
        )}
      </div>

      <div className="pv-access-section">
        <div className="pv-access-check">
          <input type="email" value={viewerEmail} onChange={e => setViewerEmail(e.target.value)} placeholder="Enter your email for additional access" className="pv-email-input" onKeyDown={e => { if (e.key === 'Enter') checkAccess(); }} />
          <button className="pv-access-code-toggle" onClick={() => setShowCodeInput(!showCodeInput)}>{showCodeInput ? '✕ Close' : 'Access Code'}</button>
          <button className="pv-check-btn" onClick={checkAccess} disabled={!viewerEmail && !viewerCode}>Check Access</button>
        </div>
        {showCodeInput && (
          <div className="pv-code-row slide-down">
            <input type="text" value={viewerCode} onChange={e => setViewerCode(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="Enter 4-digit access code" className="pv-code-input-full" maxLength={4} inputMode="numeric" onKeyDown={e => { if (e.key === 'Enter') checkAccess(); }} />
          </div>
        )}
        {accessFeedback && (
          <div className={`pv-feedback ${accessFeedback}`}>
            {accessFeedback === 'granted' && <><span className="pv-fb-icon">✓</span> Access granted. You now have authorized viewing.</>}
            {accessFeedback === 'denied' && <><span className="pv-fb-icon">ℹ</span> No additional access found. You're viewing as Public.</>}
            {accessFeedback === 'paused' && <><span className="pv-fb-icon">⏸</span> This access level is currently paused.</>}
          </div>
        )}
      </div>

      {permissions.stats && (
        <div className="pv-stats">
          <div className="pv-stat"><span className="pv-stat-val">{stats.totalCompanies}</span><span className="pv-stat-label">Companies</span></div>
          <div className="pv-stat"><span className="pv-stat-val">{stats.totalRoles}</span><span className="pv-stat-label">Roles</span></div>
          <div className="pv-stat"><span className="pv-stat-val">{stats.totalContacts}</span><span className="pv-stat-label">Contacts</span></div>
          <div className="pv-stat pv-stat-green"><span className="pv-stat-val">{stats.connectedContacts}</span><span className="pv-stat-label">Connected</span></div>
        </div>
      )}

      {permissions.companies && (
        <div className="pv-toolbar">
          {showSearchTip && (
            <div className="pv-search-tip">
              <span className="pv-tip-icon">💡</span>
              <span>Tip: You can search using the full company name or its abbreviation to quickly find results.</span>
              <button className="pv-tip-close" onClick={() => setShowSearchTip(false)}>✕</button>
            </div>
          )}
          <div className="pv-search-row">
            <div className="pv-search">
              <Icons.Search size={16} />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search companies, roles, contacts, or locations..." />
              {searchQuery && <button className="pv-search-clear" onClick={() => setSearchQuery('')}><Icons.X size={14} /></button>}
            </div>
            <div className="pv-view-toggle">
              <button className={`pv-vt-btn ${viewLayout === 'list' ? 'active' : ''}`} onClick={() => setViewLayout('list')} title="List">☰</button>
              <button className={`pv-vt-btn ${viewLayout === 'grid' ? 'active' : ''}`} onClick={() => setViewLayout('grid')} title="Grid">⊞</button>
              <button className={`pv-vt-btn ${viewLayout === 'compact' ? 'active' : ''}`} onClick={() => setViewLayout('compact')} title="Compact">≡</button>
            </div>
            {canExport && (
              <button className="pv-export-btn" onClick={handleThirdPartyExport} title="Export to Excel">
                <Icons.Download size={14} /><span>Export</span>
              </button>
            )}
          </div>
          <div className="pv-filters-row">
            {permissions.liveStatus && <MultiSelectFilter label="Status" options={['Not Applied','Applied','Interviewing','Offer','Rejected']} selected={statusFilters} onChange={setStatusFilters} placeholder="All Statuses" />}
            {permissions.contacts && <MultiSelectFilter label="Contact Status" options={['Connected','Pending']} selected={contactFilters} onChange={setContactFilters} placeholder="All Contacts" />}
            {permissions.state && uniqueStates.length > 0 && <MultiSelectFilter label="State" options={uniqueStates} selected={stateFilters} onChange={setStateFilters} placeholder="All States" searchable />}
            {permissions.city && uniqueCities.length > 0 && <MultiSelectFilter label="City" options={uniqueCities} selected={cityFilters} onChange={setCityFilters} placeholder="All Cities" searchable />}
            {permissions.updateMessage && (
              <div className="filter-group">
                <label>Update Message</label>
                <button
                  className={`update-msg-filter-btn ${updateMsgFilter ? 'active' : ''}`}
                  onClick={() => setUpdateMsgFilter(v => !v)}
                  title="Show only roles with an update message"
                >
                  {updateMsgFilter ? '✓ Has Update' : 'Has Update'}
                </button>
              </div>
            )}
            {hasActiveFilters && <button className="clear-filters" onClick={() => { setStatusFilters([]); setContactFilters([]); setStateFilters([]); setCityFilters([]); setUpdateMsgFilter(false); }} style={{alignSelf:'flex-end', marginBottom: 2}}>Clear All</button>}
          </div>
          <div className="pv-results-count">{filteredCompanies.length} of {companies.length} companies</div>
        </div>
      )}

      {permissions.companies && (
        <div className={`pv-companies pv-layout-${viewLayout}`}>
          {filteredCompanies.map(company => {
            const companyVisibleRoles = filteredRoles.filter(r => r.companyId === company.id);
            return (
              <div key={company.id} className="pv-company-card">
                <div className="pv-company-header">
                  <Icons.Building size={16} /><h3>{company.name}</h3>
                  <span className="pv-role-count">{companyVisibleRoles.length} role{companyVisibleRoles.length !== 1 ? 's' : ''}</span>
                </div>
                {permissions.notes && company.notes && (
                  <div className="pv-company-notes"><Icons.StickyNote size={12} /><span>{company.notes}</span></div>
                )}
                {permissions.roles && companyVisibleRoles.map(role => (
                  <div key={role.id} className="pv-role-row">
                    <span className="pv-role-title">{role.title}</span>
                    {permissions.liveStatus && <span className={`pv-status-badge ${STATUS_COLORS[role.status]}`}>{role.status}</span>}
                    {(permissions.state || permissions.city) && role.location && (() => {
                      const parts = role.location.split('·').map(p => p.trim());
                      const st = parts[0] || '';
                      const ct = parts[1] || '';
                      const display = [permissions.state ? st : '', permissions.city ? ct : ''].filter(Boolean).join(' · ');
                      return display ? <span className="pv-role-location"><Icons.MapPin size={12} /> {display}</span> : null;
                    })()}
                    {showDates && role.appliedDate && <span className="pv-role-date">{role.appliedDate}</span>}
                    {permissions.updateMessage && role.updateMessage && (
                      <div className="pv-role-update-msg"><Icons.MessageCircle size={12} /> {role.updateMessage}</div>
                    )}
                    {permissions.notes && role.notes && (
                      <div className="pv-role-notes"><Icons.StickyNote size={12} /><span>{role.notes}</span></div>
                    )}
                    {permissions.contacts && (
                      <div className="pv-contacts">
                        {filteredContacts.filter(c => c.roleId === role.id).map(contact => (
                          <div key={contact.id} className="pv-contact-row">
                            <span className={`pv-linkedin-dot ${contact.linkedinStatus === 'Connected' ? 'connected' : 'pending'}`} />
                            <span className="pv-contact-name">{contact.name}</span>
                            {contact.designation && <span className="pv-contact-desig">{contact.designation}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
          {filteredCompanies.length === 0 && (
            <div className="pv-empty">{searchQuery || hasActiveFilters ? 'No companies match your filters.' : 'No companies being tracked yet.'}</div>
          )}
        </div>
      )}

      <footer className="pv-footer">Powered by <strong onClick={() => navigate('/')} style={{cursor:'pointer'}}>TrackThrough</strong> · © 2026 TrackThrough. All rights reserved.</footer>
    </div>
  );
}
